import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Linking,
  Platform,
  StyleSheet,
  Clipboard,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import {
  SHARE_VISIBILITY,
  addShareAudienceMember,
  buildShareUrl,
  canManageShares,
  fetchTourPublicShare,
  isTourOwner,
  revokeShareAudienceMember,
  setShareLinkActive,
  setShareLinkModules,
  setShareLinkModuleOrder,
  setShareLinkVisibility,
  setShareManagers,
  subscribeShareLinksForTour,
  subscribeShareSettings,
  getFirestoreDb,
  DEFAULT_SHARE_MODULE_ORDER,
  normalizeOrderedModules,
  shareLinkDisplayTitle,
  shareLinkPrimaryContact,
} from '../../utils/calendarShareService';
import { canManageTourShares } from '../../utils/tourMemberPermissionsService';
import { subscribeTourInvitesForTour } from '../../utils/tourInviteService';
import { searchUsersForShare, sendShareLinkToRecipients } from '../../utils/calendarShareSendService';
import {
  formatScheduleDateLabel,
  formatScheduleSummary,
  getSchedulesForLink,
  subscribeTourShareSettings,
} from '../../utils/tourShareScheduleService';
import { useCalendarGlassStyles } from './calendarGlassStyles';
import { ThemedSwitch } from './ThemedSwitch';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function shareRecipientLabel(person) {
  const name = String(person?.name || '').trim();
  const email = normalizeEmail(person?.email);
  return name || email || 'Recipient';
}

const DAY_SHARE_OPTIONS = DEFAULT_SHARE_MODULE_ORDER;

function resolveAppBaseUrl() {
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://venuereportcard.com';
}

function copyText(text) {
  const val = String(text || '').trim();
  if (!val) return;
  if (Platform.OS === 'web' && navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(val).catch(() => {});
    return;
  }
  Clipboard.setString(val);
}

function ShareLinkCard({
  link,
  theme,
  isDarkMode,
  canManage,
  isOwner,
  ownerId,
  ownerName,
  tourName,
  db,
  onToggleActive,
  onToggleVisibility,
  onToggleModule,
  onMoveModule,
  onAddAudience,
  onRevokeAudience,
  onPreview,
  onCopy,
  onSendEmail,
  onToast,
  saving,
  linkedSchedules = [],
}) {
  const glass = useCalendarGlassStyles(theme, isDarkMode);
  const styles = useMemo(() => createCardStyles(theme, isDarkMode), [theme, isDarkMode]);
  const [expanded, setExpanded] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [emailQueue, setEmailQueue] = useState([]);
  const [emailMessage, setEmailMessage] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const searchSeq = useRef(0);
  const activeAudience = (link.audience || []).filter((a) => !a.revoked);
  const emailLog = useMemo(
    () => (Array.isArray(link.emailLog) ? [...link.emailLog].reverse().slice(0, 8) : []),
    [link.emailLog]
  );
  const queueEmails = useMemo(
    () => new Set(emailQueue.map((q) => normalizeEmail(q.email)).filter(Boolean)),
    [emailQueue]
  );
  const excludeEmails = useMemo(() => [...queueEmails], [queueEmails]);
  const url = link.token ? buildShareUrl(resolveAppBaseUrl(), link.token) : link.legacyUrl || '';
  const orderedModules = useMemo(() => normalizeOrderedModules(link.modules || []), [link.modules]);
  const disabledModules = useMemo(() => {
    const enabled = new Set(orderedModules);
    return DAY_SHARE_OPTIONS.filter((mod) => !enabled.has(mod));
  }, [orderedModules]);
  const textSecondary = isDarkMode ? '#c8c8c8' : theme.text.secondary;

  const runUserSearch = useCallback(
    async (term) => {
      setSearch(term);
      if (term.trim().length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      const seq = ++searchSeq.current;
      setSearchLoading(true);
      try {
        const rows = await searchUsersForShare(db, term, {
          excludeUserId: ownerId,
          excludeEmails,
        });
        if (seq !== searchSeq.current) return;
        setSearchResults(rows);
      } catch (err) {
        if (seq !== searchSeq.current) return;
        console.warn('Share link user search', err);
        setSearchResults([]);
      } finally {
        if (seq === searchSeq.current) setSearchLoading(false);
      }
    },
    [db, ownerId, excludeEmails]
  );

  const visibleSearchResults = useMemo(
    () => searchResults.filter((u) => !queueEmails.has(normalizeEmail(u.email))),
    [searchResults, queueEmails]
  );

  const addUserToQueue = (user) => {
    const email = normalizeEmail(user.email);
    if (!email || queueEmails.has(email)) return;
    const first = String(user.firstName || '').trim();
    const last = String(user.lastName || '').trim();
    const name = `${first} ${last}`.trim() || String(user.username || '').trim();
    setEmailQueue((prev) => [
      ...prev,
      {
        key: user.id || email,
        userId: user.id,
        email,
        name,
        phone: String(user.phone || user.phoneNumber || '').trim(),
        source: 'user',
      },
    ]);
    setSearch('');
    setSearchResults([]);
  };

  const addManualToQueue = () => {
    const email = normalizeEmail(manualEmail);
    if (!email) {
      onToast?.('Enter a valid email address.', 'error');
      return;
    }
    if (queueEmails.has(email)) {
      onToast?.('That email is already in your send list.', 'error');
      return;
    }
    setEmailQueue((prev) => [
      ...prev,
      { key: `manual-${email}`, email, name: '', source: 'manual' },
    ]);
    setManualEmail('');
  };

  const removeFromQueue = (key) => {
    setEmailQueue((prev) => prev.filter((q) => q.key !== key));
  };

  const handleSendEmail = async () => {
    if (emailQueue.length === 0) {
      onToast?.('Add at least one person to email.', 'error');
      return;
    }
    try {
      setSendingEmail(true);
      await onSendEmail(link, emailQueue, emailMessage.trim());
      setEmailQueue([]);
      setEmailMessage('');
    } catch (err) {
      onToast?.(err?.message || 'Could not send share email.', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <View style={[glass.card, styles.card]}>
      <TouchableOpacity
        style={styles.cardHeader}
        onPress={() => setExpanded((v) => !v)}
        activeOpacity={0.85}
      >
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.cardTitle} numberOfLines={1}>
            {shareLinkDisplayTitle(link)}
          </Text>
          {(() => {
            const contact = shareLinkPrimaryContact(link);
            const sub = contact.name || link.label || link.tourName || 'Tour share';
            return (
              <>
                <Text style={styles.cardSub} numberOfLines={2}>{sub}</Text>
                <Text style={styles.cardContact} numberOfLines={1}>
                  {contact.email || '—'}
                  {'  ·  '}
                  {contact.phone || '—'}
                </Text>
              </>
            );
          })()}
          {linkedSchedules.length ? (
            <View style={styles.scheduleBadgeWrap}>
              {linkedSchedules.slice(0, 2).map((s) => (
                <Text key={s.id} style={styles.scheduleBadge} numberOfLines={1}>
                  {s.kind === 'rider_reminder' ? 'Reminder' : 'Scheduled'} · {formatScheduleDateLabel(s.sendDateKey)}
                  {' · '}
                  {(s.modules || []).slice(0, 2).join(', ')}
                </Text>
              ))}
            </View>
          ) : null}
          {!expanded && link.modules?.length ? (
            <Text style={styles.modulesListCollapsed} numberOfLines={1}>
              {orderedModules.join(' · ')}
            </Text>
          ) : null}
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            onPress={() => setExpanded((v) => !v)}
            hitSlop={8}
            style={styles.iconBtn}
          >
            <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={textSecondary} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={(e) => {
              e?.stopPropagation?.();
              onPreview(url);
            }}
            hitSlop={8}
            style={styles.viewAsBtn}
            accessibilityLabel="View As"
          >
            <Ionicons name="eye-outline" size={16} color={theme.brand.accent} />
            <Text style={[styles.viewAsText, { color: theme.brand.accent }]}>View As</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={(e) => {
              e?.stopPropagation?.();
              onCopy(url);
            }}
            hitSlop={8}
            style={styles.iconBtn}
          >
            <Ionicons name="copy-outline" size={18} color={textSecondary} />
          </TouchableOpacity>
        </View>
      </TouchableOpacity>

      {expanded ? (
        <>
      {link.type === 'day' && canManage ? (
        <View style={styles.moduleWrap}>
          <Text style={styles.sectionLabel}>Shared section order</Text>
          <Text style={styles.rowHint}>Viewers see sections in this order. Use arrows to rearrange.</Text>
          {orderedModules.map((mod, idx) => (
            <View key={mod} style={styles.moduleOrderRow}>
              <Ionicons name="reorder-three-outline" size={18} color={textSecondary} />
              <Text style={styles.moduleOrderLabel}>{mod}</Text>
              <View style={styles.moduleOrderActions}>
                <TouchableOpacity
                  style={[styles.orderBtn, idx === 0 && styles.orderBtnDisabled]}
                  onPress={() => onMoveModule(link, mod, -1)}
                  disabled={idx === 0 || saving}
                  hitSlop={6}
                >
                  <Ionicons name="chevron-up" size={16} color={textSecondary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.orderBtn, idx === orderedModules.length - 1 && styles.orderBtnDisabled]}
                  onPress={() => onMoveModule(link, mod, 1)}
                  disabled={idx === orderedModules.length - 1 || saving}
                  hitSlop={6}
                >
                  <Ionicons name="chevron-down" size={16} color={textSecondary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.orderBtn}
                  onPress={() => onToggleModule(link, mod)}
                  disabled={saving || orderedModules.length <= 1}
                  hitSlop={6}
                >
                  <Ionicons name="close-circle-outline" size={16} color={theme.status.error} />
                </TouchableOpacity>
              </View>
            </View>
          ))}
          {disabledModules.length ? (
            <>
              <Text style={[styles.sectionLabel, { marginTop: 8 }]}>Add sections</Text>
              <View style={styles.moduleGrid}>
                {disabledModules.map((mod) => (
                  <TouchableOpacity
                    key={mod}
                    style={styles.moduleChip}
                    onPress={() => onToggleModule(link, mod)}
                    disabled={saving}
                  >
                    <Ionicons name="add-circle-outline" size={14} color={theme.brand.accent} />
                    <Text style={styles.moduleChipText}>{mod}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          ) : null}
        </View>
      ) : link.modules?.length ? (
        <Text style={styles.modulesList}>{orderedModules.join(' · ')}</Text>
      ) : null}

      {canManage && link.type !== 'tour' ? (
        <>
          <View style={styles.row}>
            <Text style={styles.rowLabel}>Link active</Text>
            <ThemedSwitch
              value={link.isActive !== false}
              onValueChange={(v) => onToggleActive(link, v)}
              disabled={saving}
            />
          </View>
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <Text style={styles.rowLabel}>Require account</Text>
              <Text style={styles.rowHint}>Only people you invite can view when on</Text>
            </View>
            <ThemedSwitch
              value={link.visibility === SHARE_VISIBILITY.ACCOUNT}
              onValueChange={(v) =>
                onToggleVisibility(link, v ? SHARE_VISIBILITY.ACCOUNT : SHARE_VISIBILITY.LINK)
              }
              disabled={saving}
            />
          </View>
          {link.visibility === SHARE_VISIBILITY.ACCOUNT ? (
            <View style={styles.audienceBlock}>
              <Text style={styles.sectionLabel}>Shared with</Text>
              {activeAudience.length === 0 ? (
                <Text style={styles.rowHint}>No one invited yet — add emails below.</Text>
              ) : (
                activeAudience.map((a) => (
                  <View key={a.email} style={styles.audienceRow}>
                    <Text style={styles.audienceEmail}>{a.email}</Text>
                    <TouchableOpacity onPress={() => onRevokeAudience(link, a.email)} hitSlop={8}>
                      <Ionicons name="close-circle-outline" size={18} color={theme.status.error} />
                    </TouchableOpacity>
                  </View>
                ))
              )}
              <View style={styles.addRow}>
                <TextInput
                  style={styles.input}
                  value={emailInput}
                  onChangeText={setEmailInput}
                  placeholder="email@example.com"
                  placeholderTextColor={theme.input.placeholder}
                  autoCapitalize="none"
                  keyboardType="email-address"
                />
                <TouchableOpacity
                  style={styles.addBtn}
                  onPress={() => {
                    const e = emailInput.trim();
                    if (!e) return;
                    onAddAudience(link, e);
                    setEmailInput('');
                  }}
                  disabled={saving}
                >
                  <Ionicons name="person-add-outline" size={16} color="#fff" />
                </TouchableOpacity>
              </View>
            </View>
          ) : null}

          <View style={styles.emailBlock}>
            <Text style={styles.sectionLabel}>Email link</Text>
            <Text style={styles.rowHint}>
              Search VRC members or add any email. VRC users get an email and an in-app message — not a tour invite.
            </Text>

            {emailLog.length > 0 ? (
              <View style={styles.sentLog}>
                <Text style={[styles.sectionLabel, { marginTop: 4 }]}>Recently emailed</Text>
                {emailLog.map((row) => (
                  <Text key={`${row.email}-${row.sentAt}`} style={styles.sentLogRow} numberOfLines={1}>
                    {row.name || row.email}
                    {row.name ? ` · ${row.email}` : ''}
                  </Text>
                ))}
              </View>
            ) : null}

            {emailQueue.length > 0 ? (
              <View style={styles.queueBlock}>
                {emailQueue.map((person) => (
                  <View key={person.key} style={styles.queueRow}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.queueName} numberOfLines={1}>
                        {shareRecipientLabel(person)}
                      </Text>
                      {person.name ? (
                        <Text style={styles.queueSub} numberOfLines={1}>
                          {person.email}
                        </Text>
                      ) : null}
                    </View>
                    <TouchableOpacity onPress={() => removeFromQueue(person.key)} hitSlop={8}>
                      <Ionicons name="close-circle-outline" size={18} color={theme.status.error} />
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            ) : null}

            <View style={styles.searchField}>
              <Ionicons name="search-outline" size={16} color={theme.text.tertiary} />
              <TextInput
                style={styles.searchInput}
                value={search}
                onChangeText={runUserSearch}
                placeholder="Search by name, username, or email"
                placeholderTextColor={theme.input.placeholder}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchLoading ? <ActivityIndicator size="small" color={theme.brand.accent} /> : null}
            </View>

            {search.trim().length >= 2 && !searchLoading && visibleSearchResults.length === 0 ? (
              <Text style={styles.rowHint}>No VRC members match that search.</Text>
            ) : null}

            {visibleSearchResults.length > 0 ? (
              <View style={styles.searchResults}>
                {visibleSearchResults.map((user) => {
                  const first = String(user.firstName || '').trim();
                  const last = String(user.lastName || '').trim();
                  const name = `${first} ${last}`.trim() || user.username || user.email;
                  return (
                    <TouchableOpacity
                      key={user.id}
                      style={styles.searchRow}
                      onPress={() => addUserToQueue(user)}
                    >
                      <View style={styles.searchInfo}>
                        <Text style={styles.searchRowTitle} numberOfLines={1}>
                          {name}
                        </Text>
                        <Text style={styles.searchRowSub} numberOfLines={1}>
                          {user.email}
                        </Text>
                      </View>
                      <Ionicons name="add-circle-outline" size={20} color={theme.brand.accent} />
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}

            <View style={styles.addRow}>
              <TextInput
                style={styles.input}
                value={manualEmail}
                onChangeText={setManualEmail}
                placeholder="email@example.com"
                placeholderTextColor={theme.input.placeholder}
                autoCapitalize="none"
                keyboardType="email-address"
                onSubmitEditing={addManualToQueue}
              />
              <TouchableOpacity style={styles.addBtn} onPress={addManualToQueue} disabled={saving || sendingEmail}>
                <Ionicons name="person-add-outline" size={16} color="#fff" />
              </TouchableOpacity>
            </View>

            <TextInput
              style={[styles.input, styles.messageInput]}
              value={emailMessage}
              onChangeText={setEmailMessage}
              placeholder="Optional note (included in the email)"
              placeholderTextColor={theme.input.placeholder}
              multiline
            />

            <TouchableOpacity
              style={[
                styles.sendBtn,
                (emailQueue.length === 0 || saving || sendingEmail) && styles.sendBtnDisabled,
              ]}
              onPress={handleSendEmail}
              disabled={emailQueue.length === 0 || saving || sendingEmail}
            >
              {sendingEmail ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Ionicons name="mail-outline" size={16} color="#fff" />
                  <Text style={styles.sendBtnText}>
                    {emailQueue.length === 0
                      ? 'Send link'
                      : `Send to ${emailQueue.length} recipient${emailQueue.length === 1 ? '' : 's'}`}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </>
      ) : null}

          {linkedSchedules.length ? (
            <View style={styles.scheduleDetailBlock}>
              <Text style={styles.sectionLabel}>Scheduled sends</Text>
              {linkedSchedules.map((s) => (
                <Text key={s.id} style={styles.scheduleDetailRow} numberOfLines={2}>
                  {formatScheduleSummary(s)}
                </Text>
              ))}
            </View>
          ) : null}

          {link.type === 'tour' ? (
            <Text style={styles.rowHint}>
              Tour map link — manage visibility from Tour Planner share. Updates when the tour is re-shared.
            </Text>
          ) : null}

          <Text style={styles.urlPreview} numberOfLines={2} selectable>
            {url || 'No link'}
          </Text>
        </>
      ) : null}
    </View>
  );
}

export function SharedLinksManager({
  visible,
  onClose,
  ownerId,
  ownerName,
  tourId,
  tourName,
  userId,
  userEmail,
  onToast,
  showDateKeys = [],
}) {
  const { theme, isDarkMode } = useTheme();
  const db = getFirestoreDb();
  const [links, setLinks] = useState([]);
  const [tourShare, setTourShare] = useState(null);
  const [calendarAccessSettings, setCalendarAccessSettings] = useState({
    managerIds: [],
    memberPermissions: {},
  });
  const [shareSettings, setShareSettings] = useState({ sendSchedules: [], riderReminder: {} });
  const [partyMembers, setPartyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isOwner = isTourOwner(userId, ownerId);
  const managerIds = calendarAccessSettings.managerIds || [];
  const canManage = canManageTourShares(userId, ownerId, calendarAccessSettings);

  useEffect(() => {
    if (!visible || !ownerId || !tourId) return undefined;
    setLoading(true);
    const unsubs = [
      subscribeShareLinksForTour(db, ownerId, tourId, (rows) => {
        setLinks(rows.filter((r) => r.type === 'day'));
        setLoading(false);
      }),
      subscribeShareSettings(db, ownerId, tourId, setCalendarAccessSettings),
      subscribeTourShareSettings(db, ownerId, tourId, setShareSettings),
      subscribeTourInvitesForTour(db, ownerId, tourId, (rows) => {
        setPartyMembers(rows.filter((r) => String(r.status || '').toLowerCase() === 'accepted'));
      }),
    ];
    fetchTourPublicShare(db, tourId).then(setTourShare).catch(() => setTourShare(null));
    return () => unsubs.forEach((u) => u());
  }, [visible, ownerId, tourId, db]);

  const allLinks = useMemo(() => {
    const rows = [...links];
    if (tourShare) {
      rows.unshift({
        id: `tour__${tourId}`,
        type: 'tour',
        tourId,
        tourName: tourShare.tourName,
        isActive: tourShare.isActive,
        legacyUrl: tourShare.legacyUrl,
        label: `${tourShare.tourName} (Tour Planner)`,
      });
    }
    return rows;
  }, [links, tourShare, tourId]);

  const handleToggleActive = useCallback(
    async (link, active) => {
      if (!canManage || link.type === 'tour') return;
      try {
        setSaving(true);
        await setShareLinkActive(db, ownerId, link.id, active);
        onToast?.(active ? 'Share link activated.' : 'Share link deactivated.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not update link.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleToggleVisibility = useCallback(
    async (link, visibility) => {
      if (!canManage) return;
      try {
        setSaving(true);
        await setShareLinkVisibility(db, ownerId, link.id, visibility);
        onToast?.(
          visibility === SHARE_VISIBILITY.ACCOUNT ? 'Account required to view.' : 'Anyone with the link can view.',
          'success'
        );
      } catch (err) {
        onToast?.(err?.message || 'Could not update visibility.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleToggleModule = useCallback(
    async (link, mod) => {
      if (!canManage) return;
      const current = normalizeOrderedModules(link.modules || []);
      const next = current.includes(mod)
        ? current.filter((m) => m !== mod)
        : [...current, mod];
      if (!next.length) {
        onToast?.('At least one section must stay shared.', 'error');
        return;
      }
      try {
        setSaving(true);
        await setShareLinkModules(db, ownerId, link.id, next);
        onToast?.('Shared sections updated — viewers see changes live.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not update sections.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleMoveModule = useCallback(
    async (link, mod, direction) => {
      if (!canManage || !direction) return;
      const current = normalizeOrderedModules(link.modules || []);
      const idx = current.indexOf(mod);
      if (idx < 0) return;
      const target = idx + direction;
      if (target < 0 || target >= current.length) return;
      const next = [...current];
      [next[idx], next[target]] = [next[target], next[idx]];
      try {
        setSaving(true);
        await setShareLinkModuleOrder(db, ownerId, link.id, next);
        onToast?.('Section order updated for viewers.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not reorder sections.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleAddAudience = useCallback(
    async (link, email) => {
      if (!canManage) return;
      try {
        setSaving(true);
        const wanted = normalizeEmail(email);
        const matches = wanted
          ? await searchUsersForShare(db, wanted, { excludeUserId: ownerId })
          : [];
        const match = matches.find((u) => normalizeEmail(u.email) === wanted) || null;
        const first = String(match?.firstName || '').trim();
        const last = String(match?.lastName || '').trim();
        const name = `${first} ${last}`.trim() || String(match?.username || '').trim();
        await addShareAudienceMember(db, ownerId, link.id, {
          email,
          userId: match?.id || '',
          name,
          phone: String(match?.phone || match?.phoneNumber || '').trim(),
        });
        onToast?.('Access granted.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not add person.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleRevokeAudience = useCallback(
    async (link, email) => {
      if (!canManage) return;
      try {
        setSaving(true);
        await revokeShareAudienceMember(db, ownerId, link.id, email);
        onToast?.('Access removed.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not remove access.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, onToast]
  );

  const handleToggleManager = useCallback(
    async (memberUid) => {
      if (!isOwner || !memberUid) return;
      const next = managerIds.includes(memberUid)
        ? managerIds.filter((id) => id !== memberUid)
        : [...managerIds, memberUid];
      try {
        setSaving(true);
        await setShareManagers(db, ownerId, tourId, next);
        onToast?.('Share managers updated.', 'success');
      } catch (err) {
        onToast?.(err?.message || 'Could not update managers.', 'error');
      } finally {
        setSaving(false);
      }
    },
    [isOwner, managerIds, db, ownerId, tourId, onToast]
  );

  const handlePreview = useCallback((url) => {
    if (!url) return;
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(url).catch(() => {});
    }
  }, []);

  const handleCopy = useCallback(
    (url) => {
      copyText(url);
      onToast?.('Link copied.', 'success');
    },
    [onToast]
  );

  const handleSendShareEmail = useCallback(
    async (link, recipients, message) => {
      if (!canManage || !link?.id || !link?.token) {
        throw new Error('Share link is not ready yet.');
      }
      const shareUrl = buildShareUrl(resolveAppBaseUrl(), link.token);
      setSaving(true);
      try {
        await sendShareLinkToRecipients({
          db,
          ownerId,
          ownerName: ownerName || 'Tour team',
          linkId: link.id,
          shareToken: link.token,
          shareUrl,
          tourName: link.tourName || tourName,
          dateKey: link.dateKey,
          dateLabel: link.dateKey,
          visibility: link.visibility,
          recipients,
          message,
        });
        const count = recipients.length;
        onToast?.(
          count === 1 ? 'Share link emailed.' : `Share link emailed to ${count} people.`,
          'success'
        );
      } finally {
        setSaving(false);
      }
    },
    [canManage, db, ownerId, ownerName, tourName, onToast]
  );

  const styles = useMemo(() => createPanelStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!visible) return null;

  return (
    <View style={styles.overlay}>
      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <View style={{ flex: 1 }}>
            <Text style={styles.panelTitle}>Shared links</Text>
            <Text style={styles.panelSub}>{tourName || 'Tour'} · updates appear live for viewers</Text>
          </View>
          <TouchableOpacity onPress={onClose} hitSlop={12} style={styles.closeBtn}>
            <Ionicons name="close" size={22} color={isDarkMode ? '#f2f2f2' : theme.text.primary} />
          </TouchableOpacity>
        </View>

        {!canManage ? (
          <Text style={styles.readOnlyNote}>You can view shared links for this tour. Only the tour creator or assigned share managers can edit them.</Text>
        ) : null}

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {loading ? (
            <ActivityIndicator color={theme.brand.accent} style={{ marginTop: 24 }} />
          ) : allLinks.length === 0 ? (
            <View style={styles.empty}>
              <Ionicons name="link-outline" size={40} color={theme.text.tertiary} />
              <Text style={styles.emptyTitle}>No shared links yet</Text>
              <Text style={styles.emptyText}>
                Use the share button on a day to create a link. It will show up here where you can turn it on or off, invite people, and preview what they see.
              </Text>
            </View>
          ) : (
            allLinks.map((link) => (
              <ShareLinkCard
                key={link.id}
                link={link}
                theme={theme}
                isDarkMode={isDarkMode}
                canManage={canManage && link.type !== 'tour'}
                isOwner={isOwner}
                ownerId={ownerId}
                ownerName={ownerName}
                tourName={tourName}
                db={db}
                onToggleActive={handleToggleActive}
                onToggleVisibility={handleToggleVisibility}
                onToggleModule={handleToggleModule}
                onMoveModule={handleMoveModule}
                onAddAudience={handleAddAudience}
                onRevokeAudience={handleRevokeAudience}
                onPreview={handlePreview}
                onCopy={handleCopy}
                onSendEmail={handleSendShareEmail}
                onToast={onToast}
                saving={saving}
                linkedSchedules={getSchedulesForLink(link, shareSettings, showDateKeys)}
              />
            ))
          )}

          {isOwner && partyMembers.length > 0 ? (
            <View style={styles.managersSection}>
              <Text style={styles.sectionTitle}>Share managers</Text>
              <Text style={styles.managerHint}>
                Tour party members who can activate links and manage who they&apos;re shared with. Only you can change this list.
              </Text>
              {partyMembers.map((m) => {
                const mid = m.inviteeId || '';
                const label = m.inviteeName || m.inviteeEmail || 'Member';
                const isMgr = managerIds.includes(mid);
                if (!mid) return null;
                return (
                  <View key={m.id || mid} style={styles.managerRow}>
                    <Text style={styles.managerName}>{label}</Text>
                    <ThemedSwitch
                      value={isMgr}
                      onValueChange={() => handleToggleManager(mid)}
                      disabled={saving}
                    />
                  </View>
                );
              })}
            </View>
          ) : null}
        </ScrollView>
      </View>
    </View>
  );
}

function createPanelStyles(theme, isDarkMode) {
  const textPrimary = isDarkMode ? '#f2f2f2' : theme.text.primary;
  const textSecondary = isDarkMode ? '#c8c8c8' : theme.text.secondary;
  const textTertiary = isDarkMode ? '#a0a0a0' : theme.text.tertiary;

  return StyleSheet.create({
    overlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.overlay,
      zIndex: 50,
      padding: 16,
      paddingTop: 12,
    },
    panel: {
      flex: 1,
      maxWidth: 720,
      alignSelf: 'center',
      width: '100%',
      backgroundColor: isDarkMode ? theme.background.secondary : theme.background.elevated,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && isDarkMode
        ? { boxShadow: '0 24px 64px rgba(0,0,0,0.55)' }
        : {}),
    },
    panelHeader: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingHorizontal: 16,
      paddingVertical: 14,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
      gap: 12,
      backgroundColor: isDarkMode ? theme.background.tertiary : theme.background.elevated,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: '800',
      color: textPrimary,
    },
    panelSub: {
      fontSize: 12,
      color: textSecondary,
      marginTop: 2,
    },
    closeBtn: {
      padding: 4,
    },
    readOnlyNote: {
      fontSize: 12,
      color: textSecondary,
      paddingHorizontal: 16,
      paddingTop: 10,
      lineHeight: 17,
    },
    scroll: { flex: 1 },
    scrollContent: { padding: 16, paddingBottom: 32, gap: 12 },
    empty: { alignItems: 'center', paddingVertical: 32, gap: 8, paddingHorizontal: 12 },
    emptyTitle: { fontSize: 16, fontWeight: '800', color: textPrimary },
    emptyText: { fontSize: 13, color: textSecondary, textAlign: 'center', lineHeight: 19 },
    managersSection: {
      marginTop: 8,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
      gap: 8,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: '800',
      color: textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    managerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 6,
    },
    managerName: { fontSize: 14, fontWeight: '600', color: textPrimary },
    managerHint: { fontSize: 11, color: textSecondary, lineHeight: 15 },
  });
}

function createCardStyles(theme, isDarkMode) {
  const textPrimary = isDarkMode ? '#f2f2f2' : theme.text.primary;
  const textSecondary = isDarkMode ? '#c8c8c8' : theme.text.secondary;
  const textTertiary = isDarkMode ? '#a0a0a0' : theme.text.tertiary;
  const chipBg = isDarkMode ? 'rgba(255,255,255,0.06)' : theme.background.secondary;
  const rowBg = isDarkMode ? 'rgba(255,255,255,0.04)' : theme.background.secondary;

  return StyleSheet.create({
    card: { gap: 10 },
    cardHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
    cardTitle: { fontSize: 15, fontWeight: '800', color: textPrimary },
    cardSub: { fontSize: 12, color: textSecondary, marginTop: 2 },
    cardContact: { fontSize: 11, color: textTertiary, marginTop: 2 },
    scheduleBadgeWrap: { marginTop: 6, gap: 2 },
    scheduleBadge: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.brand.accent,
    },
    modulesListCollapsed: { fontSize: 11, color: textTertiary, marginTop: 4 },
    scheduleDetailBlock: { gap: 4, marginTop: 4 },
    scheduleDetailRow: { fontSize: 11, color: textSecondary, lineHeight: 16 },
    headerActions: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    viewAsBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      height: 34,
      borderRadius: 8,
      paddingHorizontal: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: chipBg,
    },
    viewAsText: { fontSize: 11, fontWeight: '700' },
    iconBtn: {
      width: 34,
      height: 34,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: chipBg,
    },
    sectionLabel: {
      fontSize: 10,
      fontWeight: '800',
      color: textTertiary,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginBottom: 4,
    },
    moduleWrap: { gap: 6 },
    moduleOrderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: rowBg,
    },
    moduleOrderLabel: {
      flex: 1,
      fontSize: 12,
      fontWeight: '700',
      color: textPrimary,
    },
    moduleOrderActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 2,
    },
    orderBtn: {
      width: 28,
      height: 28,
      borderRadius: 7,
      alignItems: 'center',
      justifyContent: 'center',
    },
    orderBtnDisabled: { opacity: 0.35 },
    moduleGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    moduleChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 5,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: chipBg,
    },
    moduleChipOn: {
      borderColor: theme.brand.accent,
      backgroundColor: `${theme.brand.accent}18`,
    },
    moduleChipText: { fontSize: 10, fontWeight: '700', color: textSecondary },
    modulesList: { fontSize: 12, color: textSecondary, lineHeight: 17 },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    rowLabel: { fontSize: 14, fontWeight: '700', color: textPrimary },
    rowHint: { fontSize: 11, color: textSecondary, marginTop: 2, lineHeight: 15 },
    audienceBlock: { gap: 6 },
    audienceRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 4,
    },
    audienceEmail: { fontSize: 13, color: textPrimary },
    addRow: { flexDirection: 'row', gap: 8, marginTop: 4 },
    input: {
      flex: 1,
      borderWidth: 1,
      borderColor: theme.input.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      fontSize: 14,
      color: theme.input.text,
      backgroundColor: theme.input.background,
    },
    addBtn: {
      width: 40,
      height: 40,
      borderRadius: 8,
      backgroundColor: theme.brand.accent,
      alignItems: 'center',
      justifyContent: 'center',
    },
    emailBlock: { gap: 8, marginTop: 4 },
    sentLog: { gap: 2 },
    sentLogRow: { fontSize: 11, color: textSecondary },
    queueBlock: { gap: 4 },
    queueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: rowBg,
    },
    queueName: { fontSize: 13, fontWeight: '700', color: textPrimary },
    queueSub: { fontSize: 11, color: textSecondary, marginTop: 1 },
    searchField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      borderWidth: 1,
      borderColor: theme.input.border,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 6,
      backgroundColor: theme.input.background,
    },
    searchInput: {
      flex: 1,
      fontSize: 14,
      color: theme.input.text,
      paddingVertical: 4,
      ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    searchResults: { gap: 4 },
    searchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.ui.border,
      backgroundColor: rowBg,
    },
    searchInfo: { flex: 1, minWidth: 0 },
    searchRowTitle: { fontSize: 13, fontWeight: '700', color: textPrimary },
    searchRowSub: { fontSize: 11, color: textSecondary, marginTop: 1 },
    messageInput: { minHeight: 56, textAlignVertical: 'top' },
    sendBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: theme.brand.accent,
    },
    sendBtnDisabled: { opacity: 0.45 },
    sendBtnText: { fontSize: 13, fontWeight: '800', color: '#fff' },
    urlPreview: {
      fontSize: 11,
      color: textTertiary,
      fontFamily: Platform.OS === 'web' ? 'monospace' : undefined,
    },
  });
}
